// Reception dashboard — fixtures (RON-5469).
//
// ⚠ THESE ARE IN THE API'S OWN SHAPE (snake_case, nested), not the module's.
//
// That is deliberate and it differs from `visitorMockData.js`, which holds already-flattened rows.
// Keeping the fixtures in wire format means `receptionApiMapper.js` runs on every mocked read, so
// the mapping is exercised in the browser during design review rather than only in its unit test.
// When `USE_RECEPTION_MOCKS` goes false, nothing downstream changes shape.
//
// Names and companies mirror the prototype's board so a side-by-side review lines up.

export const USE_RECEPTION_MOCKS = false;

// §2 — every call is scoped to one site, and the switcher is one-at-a-time.
// `reception_pin_set` decides whether the lock screen draws a PIN box at all (§7b).
export const MOCK_SITES = [
  {
    id: "site-dublin",
    name: "Northwall Quay HQ",
    address: "North Wall Quay, Dublin 1",
    timezone: "Europe/Dublin",
    location: { lat: 53.3478, lng: -6.2297 },
    reception_pin_set: true,
  },
  {
    id: "site-cork",
    name: "Cork Office",
    address: "Lapps Quay, Cork",
    timezone: "Europe/Dublin",
    location: { lat: 51.8969, lng: -8.4693 },
    reception_pin_set: false,
  },
  {
    id: "site-london",
    name: "London Office",
    address: "10 Bishopsgate, London EC2",
    timezone: "Europe/London",
    location: { lat: 51.5155, lng: -0.081 },
    reception_pin_set: true,
  },
];

// §4b — the switcher's per-site badge. Wire format, keyed to the ids above.
//
// `site-london` is deliberately all zeros. The contract is explicit that an empty site reports
// ZEROS RATHER THAN AN ABSENT ROW, because an absent row would make the switcher say "counts not
// available" for a building that is simply empty. The fixture carries that case so the screen is
// exercised against it rather than only against sites with people in them.
export const MOCK_SITE_COUNTS = [
  { id: "site-dublin", on_site: 47, expected: 12 },
  { id: "site-cork", on_site: 8, expected: 3 },
  { id: "site-london", on_site: 0, expected: 0 },
];

// The board's day. Fixed rather than "today" so a screenshot taken next month still shows a full
// board — the same reason the prototype pins 28 Apr 2026.
export const MOCK_TODAY = "2026-04-28";

// The PIN the mock's unlock endpoint accepts (§7b).
//
// It lives HERE, in the fixtures, and not in the lock component — which is the whole point of the
// exercise. The real secret is held server-side precisely because the browser at a locked desk
// still has a valid session, so a check written in the client is one a passer-by skips by
// reloading the page. When `USE_RECEPTION_MOCKS` goes false this constant is deleted and nothing
// on the client ever knows a PIN.
export const MOCK_RECEPTION_PIN = "4821";

const row = (overrides) => ({
  booking_code: "YTJ6A2L3",
  company: null,
  email: null,
  kind: "visitor",
  is_vip: false,
  is_walk_in: false,
  status: "expected",
  is_overstay: false,
  overstay_by: null,
  building: null,
  purpose: null,
  start_date: MOCK_TODAY,
  end_date: MOCK_TODAY,
  arrival_time: "09:00:00",
  expected_out: null,
  checked_in_at: null,
  checked_in_at_local: null,
  checked_out_at: null,
  checked_out_at_local: null,
  personnel: 1,
  contractor: null,
  // Only the two keys the kiosk actually writes. `hs_acknowledged` and `nda_signed` are absent
  // everywhere in these fixtures ON PURPOSE — see the mapper's note. A fixture that invented them
  // would hide the "Not recorded yet" state from every screenshot.
  compliance: { photo_captured: false, badge_number: null, badge_state: null },
  can_check_in: true,
  can_check_out: false,
  ...overrides,
});

export const MOCK_ROWS = [
  row({
    id: "r-sarah",
    name: "Sarah Mitchell",
    company: "Acme Corp",
    email: "s.mitchell@acmecorp.com",
    host: "Michael Walsh",
    purpose: "Quarterly board review",
    status: "checked_in",
    arrival_time: "09:30:00",
    expected_out: "17:00:00",
    checked_in_at: `${MOCK_TODAY} 08:30:12`,
    checked_in_at_local: `${MOCK_TODAY} 09:30:12`,
    compliance: { photo_captured: true, badge_number: "V-2026-0428-001", badge_state: "active" },
    can_check_in: false,
    can_check_out: true,
  }),
  row({
    id: "r-jane",
    name: "Jane Lawler",
    company: "Bank of Ireland",
    email: "j.lawler@boi.example",
    host: "Áine Howard",
    purpose: "CEO briefing",
    status: "checked_in",
    is_vip: true,
    arrival_time: "10:00:00",
    expected_out: "12:30:00",
    checked_in_at: `${MOCK_TODAY} 08:58:40`,
    checked_in_at_local: `${MOCK_TODAY} 09:58:40`,
    compliance: { photo_captured: true, badge_number: "V-2026-0428-002", badge_state: "active" },
    can_check_in: false,
    can_check_out: true,
  }),
  row({
    id: "r-patrick",
    name: "Patrick Kelly",
    company: "Department of Finance",
    host: "Áine Howard",
    status: "checked_in",
    arrival_time: "10:15:00",
    expected_out: "16:00:00",
    checked_in_at: `${MOCK_TODAY} 09:14:02`,
    checked_in_at_local: `${MOCK_TODAY} 10:14:02`,
    compliance: { photo_captured: true, badge_number: "V-2026-0428-003", badge_state: "active" },
    can_check_in: false,
    can_check_out: true,
  }),
  row({
    id: "r-john",
    name: "John Doyle",
    company: null,
    host: "David Murray",
    purpose: "Parcel drop",
    status: "checked_in",
    is_walk_in: true,
    arrival_time: "09:45:00",
    checked_in_at: `${MOCK_TODAY} 08:45:31`,
    checked_in_at_local: `${MOCK_TODAY} 09:45:31`,
    compliance: { photo_captured: false, badge_number: "V-2026-0428-004", badge_state: "active" },
    can_check_in: false,
    can_check_out: true,
  }),
  // THE OVERSTAY. Still `checked_in` — overstay is a flag, never a status, and the person stays
  // on the board and on the muster until check-out is confirmed.
  row({
    id: "r-tom",
    name: "Tom Jenkins",
    company: "Lamb Lawrence",
    host: "Michael Walsh",
    status: "checked_in",
    is_overstay: true,
    overstay_by: 74,
    arrival_time: "08:00:00",
    expected_out: "13:00:00",
    checked_in_at: `${MOCK_TODAY} 06:58:19`,
    checked_in_at_local: `${MOCK_TODAY} 07:58:19`,
    compliance: { photo_captured: true, badge_number: "V-2026-0428-005", badge_state: "active" },
    can_check_in: false,
    can_check_out: true,
  }),
  row({
    id: "r-mark",
    name: "Mark Phillips",
    host: "Sarah Casey",
    status: "checked_in",
    is_walk_in: true,
    arrival_time: "11:20:00",
    checked_in_at: `${MOCK_TODAY} 10:20:44`,
    checked_in_at_local: `${MOCK_TODAY} 11:20:44`,
    can_check_in: false,
    can_check_out: true,
  }),
  // A contractor. `purpose` carries the work description for this kind.
  row({
    id: "r-declan",
    name: "Declan Murphy",
    company: "Murphy Electrical",
    email: "declan@murphyelectrical.ie",
    kind: "contractor",
    host: "Michael Walsh",
    purpose: "Rewiring — second floor plant room",
    arrival_time: "13:00:00",
    expected_out: "17:30:00",
    personnel: 3,
    contractor: {
      id: "c-declan",
      trade: "Electrical",
      status: "vetted",
      vetted_until: "2027-06-30",
      is_provisional: false,
    },
  }),
  // A PROVISIONAL contractor — check-in must be refused with the reason in words (§6).
  row({
    id: "r-lisa",
    name: "Lisa Murphy",
    company: "EY",
    kind: "contractor",
    host: "Michael Walsh",
    purpose: "Audit fieldwork",
    arrival_time: "14:00:00",
    personnel: 2,
    contractor: {
      id: "c-lisa",
      trade: "Audit",
      status: "waiting",
      vetted_until: null,
      is_provisional: true,
    },
    can_check_in: false,
  }),
  row({
    id: "r-robert",
    name: "Robert Cunningham",
    company: "Department of Finance",
    host: "Áine Howard",
    is_vip: true,
    arrival_time: "15:00:00",
    expected_out: "17:00:00",
  }),
  row({
    id: "r-aoife",
    name: "Aoife O'Brien",
    company: "EY",
    host: "Sarah Casey",
    arrival_time: "15:30:00",
  }),
  row({
    id: "r-karol",
    name: "Karol Nowak",
    company: "IBM",
    host: "Liam Byrne",
    arrival_time: "16:00:00",
  }),
  // Already gone — `checked_out` rows stay on the day's board.
  row({
    id: "r-priya",
    name: "Priya Sharma",
    company: "Nova Tech",
    host: "Liam Byrne",
    status: "checked_out",
    arrival_time: "09:00:00",
    expected_out: "11:00:00",
    checked_in_at: `${MOCK_TODAY} 07:57:10`,
    checked_in_at_local: `${MOCK_TODAY} 08:57:10`,
    checked_out_at: `${MOCK_TODAY} 10:04:55`,
    checked_out_at_local: `${MOCK_TODAY} 11:04:55`,
    compliance: { photo_captured: true, badge_number: "V-2026-0428-006", badge_state: "returned" },
    can_check_in: false,
    can_check_out: false,
  }),
];

// A different day, so the date arrows have somewhere to go and the "Expected" tab can be seen on
// its own. Yesterday is all closed out; tomorrow is all still expected.
export const MOCK_ROWS_BY_DATE = {
  [MOCK_TODAY]: MOCK_ROWS,
  "2026-04-27": [
    row({
      id: "r-david",
      name: "David Chen",
      company: "Microsoft",
      host: "Liam Byrne",
      status: "checked_out",
      start_date: "2026-04-27",
      end_date: "2026-04-27",
      arrival_time: "10:00:00",
      checked_in_at: "2026-04-27 09:58:00",
      checked_out_at: "2026-04-27 15:30:00",
      can_check_in: false,
      can_check_out: false,
    }),
    row({
      id: "r-maria",
      name: "Maria Rodriguez",
      company: "BNP Paribas",
      host: "Áine Howard",
      is_vip: true,
      status: "checked_out",
      start_date: "2026-04-27",
      end_date: "2026-04-27",
      arrival_time: "11:00:00",
      checked_in_at: "2026-04-27 10:55:00",
      checked_out_at: "2026-04-27 14:00:00",
      can_check_in: false,
      can_check_out: false,
    }),
  ],
  "2026-04-29": [
    row({
      id: "r-hassan",
      name: "Hassan Al-Rahman",
      company: "Goldman Sachs",
      host: "Áine Howard",
      is_vip: true,
      start_date: "2026-04-29",
      end_date: "2026-04-29",
      arrival_time: "09:30:00",
    }),
    row({
      id: "r-niall",
      name: "Niall Burke",
      company: "Deloitte",
      host: "Liam Byrne",
      start_date: "2026-04-29",
      end_date: "2026-04-29",
      arrival_time: "14:00:00",
    }),
  ],
};

// §5 — the walk-in dialog's host picker. Every ACTIVE EMPLOYEE, not only designated hosts: a
// visitor asks for a person by name and that person need not hold the visitor capability.
// `is_host` marks the designated ones so the screen can sort or badge them.
export const MOCK_HOSTS = [
  { id: "h-michael", name: "Michael Walsh", email: "michael.walsh@example.ie", is_host: true },
  { id: "h-sarah", name: "Sarah Casey", email: "sarah.casey@example.ie", is_host: true },
  { id: "h-liam", name: "Liam Byrne", email: "liam.byrne@example.ie", is_host: true },
  { id: "h-aine", name: "Áine Howard", email: "aine.howard@example.ie", is_host: true },
  { id: "h-david", name: "David Murray", email: "david.murray@example.ie", is_host: false },
];

// ── Un-vetted contractor approval — Phase H (answers-to-frontend-17 and -18, 22-October) ─────
//
// REAL NOW. This block was a PROPOSAL of ours between 19 and 22-October: we designed the shape,
// built against it, and registered every field as a dark field. The Laravel team then shipped
// Phase H, and it differs from our guess in nine ways — the list instead of one object, the
// approval id instead of the visit id, two of our four reason codes, and a mandatory note we did
// not have. This fixture is now THEIRS; ours is in git history and in
// `RON-5469_approval-flow-ask_19-Oct.md`.
//
// Worth keeping in mind next time: nothing we invented was unreasonable, and almost none of it
// matched. Guessing a contract buys a reviewable screen and costs a day of rework.
//
// WHAT THE FLOW IS. A contractor who is not fully vetted presents at the kiosk. The kiosk does
// NOT let them proceed — "a contractor never lets themselves in". A HARD gap (no vetting,
// nothing submitted, a document marked not overridable, or the policy off) is simply refused and
// there is nothing to approve. A SOFT gap stops the kiosk and raises a request here.
//
// THE DECISION CAN BE MADE IN TWO PLACES — at the kiosk behind a staff PIN, or on this
// dashboard. That is why the writes are idempotent and why a second decider gets a 409: two
// people can be looking at the same request at the same time.
//
// WHY THERE IS NO PIN BOX HERE. The PIN belongs to the kiosk, which stands unattended in a
// lobby. This screen is already behind a signed-in session and its own idle lock (§7b).
export const MOCK_ENTRY_APPROVALS = [
  {
    // STABLE, and it is the APPROVAL's id — not the visit's. It addresses the two write
    // endpoints and de-duplicates the list across a refresh.
    id: "ea-aoife",
    visit_id: "r-aoife",
    name: "Aoife Ryan",
    // Sent, rather than derived from the name. We have `getInitials` and used it before; the
    // server's answer wins, because it is what the kiosk and the badge will print.
    initials: "AR",
    company: "Ryan Facilities",
    site: "Northwall Quay",
    kiosk: "Northwall Quay kiosk",
    host: "Conor Walsh",
    // ALWAYS FALSE, and the contract is explicit about why: "the decision was a dashboard alert
    // only, no email to the host". Our first build said "host Michael Walsh notified" in the
    // banner on the strength of a flag we invented. It was never true.
    host_notified: false,
    outstanding: [
      {
        id: "oa-insurance",
        label: "Public liability insurance expired",
        // `expired` / `missing` / … — the gap KIND, beside the sentence. Carried so a future
        // screen can group or icon them without parsing English.
        gap: "expired",
      },
      { id: "oa-method", label: "Method statement not submitted", gap: "missing" },
    ],
    // The same list as plain strings, for anywhere a one-line summary is wanted. The banner uses
    // it; the dialog uses `outstanding`, because each one needs its own tick and its own id.
    outstanding_labels: [
      "Public liability insurance expired",
      "Method statement not submitted",
    ],
    requested_at: `${MOCK_TODAY} 09:12:00`,
    requested_at_local: `${MOCK_TODAY} 10:12:00`,
  },
];

// `off` / `reception` / `reception_host`. Carried on the board beside the approvals.
//
// `off` means an un-vetted contractor is a hard stop at the kiosk and there is nothing to
// approve — so the board sends no approvals and the banner never draws. We read it rather than
// inferring from an empty list, because "nobody waiting" and "this site does not allow
// approvals" are different states and only one of them is temporary.
export const MOCK_UNVETTED_POLICY = "reception";

// The contractor the banner is about. Not in `MOCK_ROWS` — she has not been checked in, and the
// whole point is that she cannot be until somebody approves it.
//
// `can_check_in` is FALSE. The board already refuses an uncleared contractor four separate ways
// (§6), and approving does not bypass that refusal — it changes what the server will allow on
// the next press. The screen must not enable its own button on the strength of a local tick.
export const MOCK_APPROVAL_ROW = row({
  id: "r-aoife",
  name: "Aoife Ryan",
  company: "Ryan Facilities",
  kind: "contractor",
  host: "Michael Walsh",
  arrival_time: "10:00:00",
  purpose: "Quarterly fire-door inspection",
  contractor: {
    id: "c-ryan",
    trade: "Fire safety",
    status: "expired",
    vetted_until: "2026-03-31",
    // The API's meaning of the word: NOT cleared, refuse entry. The badge this flow issues is
    // the opposite thing and is called `escort_required` for exactly that reason.
    is_provisional: true,
  },
  can_check_in: false,
  can_check_out: false,
});

// Added to today's board rather than declared inside it, because `row()` and `MOCK_ROWS` are
// both defined above and the block above has to sit next to the rest of the approval fixture to
// be readable. `MOCK_ROWS_BY_DATE[MOCK_TODAY]` is the same array, so one push serves both.
MOCK_ROWS.push(MOCK_APPROVAL_ROW);
